/**
 * Account (MyHobbii) URLs used for cross-app navigation.
 * When the user is outside the account SPA, we need full URLs.
 * Hash paths must stay in sync with MyHobbii route definitions in routes.ts.
 * Unit tests (accountUrls.unit.ts) ensure they remain in sync.
 *
 * Hosted at /pages/account (a theme page template) rather than Shopify's
 * /account route, which is owned by Shopify's new customer accounts portal.
 */
/**
 * The account SPA's path segment, market prefix aside. Exported on its own
 * (rather than folded into getAccountBase()) so account-base-links.unit.ts
 * can import a real value here instead of hardcoding its own -- importing
 * this constant doesn't touch `window`, only calling getAccountBase() does,
 * so this stays testable in Node while still being the actual source of
 * truth this file's other consumers and the sync test are meant to track.
 */
export const ACCOUNT_PATH_SEGMENT = 'pages/account';

/**
 * Locale-aware base path for the account SPA.
 * window.Shopify.routes.root carries the active market/locale prefix (e.g.
 * "/en-ca/" or "/"), so both the router history base and cross-app links keep
 * the customer on their market. Without the prefix, Vue Router rewrites the
 * pathname to the default locale on navigation. Read at call time so it
 * reflects the market of the page the app is running on.
 */
export const getAccountBase = (): string =>
  `${window.Shopify.routes.root}${ACCOUNT_PATH_SEGMENT}`;

/**
 * Path for stash patterns list (hash segment).
 * Must match route path in MyHobbii routes.ts.
 * Unit tests ensure they stay in sync.
 */
const STASH_PATTERNS_PATH = '/stash/patterns';

/** Full URL for the stash patterns page. Use for window.location from outside the account app. */
export const getStashPatternsUrl = (): string =>
  `${getAccountBase()}#${STASH_PATTERNS_PATH}`;
